import {
  I2cCheckbox,
  I2cCheckboxGroup,
  I2cListItem,
  I2cRadioButton,
  I2cRadioGroup,
  I2cInput,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { secureVPNOptions, vpnSetupOptions } from "./stringUtils";
import { validationSchemaVPNSetup } from "./ValidationSchema";

const VPNSetup = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchemaVPNSetup),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      virtual_private_network_secure_vpn_setup_with_i2c: "no",
      virtual_private_network_secure_communication_and_connectivity_via_secure_vpn: [],
      virtual_private_network_secure_communication_and_connectivity_via_secure_vpn_other: "",
    },
  });

  const {
    formState: { errors },
  } = methods;

  const fields = [
    "virtual_private_network_secure_vpn_setup_with_i2c",
    "virtual_private_network_secure_communication_and_connectivity_via_secure_vpn",
    "virtual_private_network_secure_communication_and_connectivity_via_secure_vpn_other",
  ];

  // Handle value changes and update parent state
  const handleValueChanges = React.useCallback(() => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      5: {
        ...addFormData["5"],
        "5_0": { ...values },
      },
    });
  }, [methods, setAddFormData, addFormData]);

  // Load existing form data
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[5]?.["5_0"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form validation state when errors change
  useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) {
      setFormValidStates("5_0", true);
    } else {
      setFormValidStates("5_0", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const handleRadioChange = (value) => {
    methods.setValue("virtual_private_network_secure_vpn_setup_with_i2c", value, { shouldValidate: true });
    
    // Clear dependent fields if not "yes"
    if (value !== "yes") {
      methods.setValue("virtual_private_network_secure_communication_and_connectivity_via_secure_vpn", []);
      methods.setValue("virtual_private_network_secure_communication_and_connectivity_via_secure_vpn_other", "");
    }
    
    handleValueChanges();
  };

  const handleCheckboxToggle = (value) => {
    let updated = [...methods.watch("virtual_private_network_secure_communication_and_connectivity_via_secure_vpn")];
    
    if (updated.includes(value)) {
      updated = updated.filter((item) => item !== value);
      // Clear "other" field when "other" checkbox is unchecked
      if (value === "other") {
        methods.setValue("virtual_private_network_secure_communication_and_connectivity_via_secure_vpn_other", "");
      }
    } else {
      updated.push(value);
    }
    
    methods.setValue("virtual_private_network_secure_communication_and_connectivity_via_secure_vpn", updated, { shouldValidate: true });
    handleValueChanges();
  };

  const handleInputChange = (fieldName, value) => {
    methods.setValue(fieldName, value, { shouldValidate: true });
    handleValueChanges();
  };

  const isVPNRequired = methods.watch("virtual_private_network_secure_vpn_setup_with_i2c") === "yes";

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-md">
          <div i2c-grid="12">
            <div className="sub-section">
              <div className="sub-heading mb-16">
                Secure Virtual Private Network (VPN) Setup with i2c?
              </div>
              <I2cRadioGroup
                label=""
                value={methods.watch("virtual_private_network_secure_vpn_setup_with_i2c")}
                name="virtual_private_network_secure_vpn_setup_with_i2c"
                onI2cChange={(e) => handleRadioChange(e.target.value)}
              >
                {vpnSetupOptions.map((option) => (
                  <I2cRadioButton key={option.value} value={option.value}>
                    {option.label}
                  </I2cRadioButton>
                ))}
              </I2cRadioGroup>
              {errors.virtual_private_network_secure_vpn_setup_with_i2c && (
                <ErrorMessage message={errors.virtual_private_network_secure_vpn_setup_with_i2c.message} />
              )}
            </div>
            {isVPNRequired && (
              <div className="sub-section">
                <div className="sub-heading mb-16">
                  Communication & Connectivity via Secure VPN
                </div>
                <I2cCheckboxGroup className="sub-section">
                  <div i2c-grid="grid gap-sm">
                    {secureVPNOptions.map((tech, index) => (
                      <I2cListItem
                        key={index}
                        i2c-grid="4@md"
                        variant="receipt"
                        label={tech.label}
                        value=""
                        type="checkbox"
                        checked={methods.watch("virtual_private_network_secure_communication_and_connectivity_via_secure_vpn").includes(tech.value)}
                        onI2cCheckboxClicked={() =>
                          handleCheckboxToggle(tech.value)
                        }
                      >
                        <I2cCheckbox
                          slot="prefix"
                          name="virtual_private_network_secure_communication_and_connectivity_via_secure_vpn"
                          value={tech.value}
                          label=""
                          filled
                          checked={methods.watch("virtual_private_network_secure_communication_and_connectivity_via_secure_vpn").includes(tech.value)}
                          className="customCheckbox"
                        ></I2cCheckbox>
                      </I2cListItem>
                    ))}
                  </div>
                </I2cCheckboxGroup>
                {errors.virtual_private_network_secure_communication_and_connectivity_via_secure_vpn && (
                  <ErrorMessage message={errors.virtual_private_network_secure_communication_and_connectivity_via_secure_vpn.message} />
                )}
                {methods
                  .watch(
                    "virtual_private_network_secure_communication_and_connectivity_via_secure_vpn",
                  )
                  .includes("other") && (
                  <div i2c-grid="grid 4@md">
                    <div className="single-field-wrapper">
                      <I2cInput
                        className="single-input-field"
                        placeholder="Please Specify"
                        size="large"
                        label="Enter Details"
                        name="virtual_private_network_secure_communication_and_connectivity_via_secure_vpn_other"
                        value={methods.watch(
                          "virtual_private_network_secure_communication_and_connectivity_via_secure_vpn_other",
                        )}
                        onI2cInput={(e) =>
                          handleInputChange(
                            "virtual_private_network_secure_communication_and_connectivity_via_secure_vpn_other",
                            e.target.value,
                          )
                        }
                        invalid={
                          errors.virtual_private_network_secure_communication_and_connectivity_via_secure_vpn_other
                            ? true
                            : false
                        }
                      />
                      {errors.virtual_private_network_secure_communication_and_connectivity_via_secure_vpn_other && (
                        <ErrorMessage
                          message={
                            errors
                              .virtual_private_network_secure_communication_and_connectivity_via_secure_vpn_other
                              .message
                          }
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default VPNSetup;
